import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Waves, Languages, CheckCircle2, AlertTriangle, Mail, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { checkCompliance, getReadingStatus } from '../utils/complianceRanges';
import { calculateSaturationIndex, getLSIStatus } from '../utils/saturationIndex';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicViewPage = () => {
  const { poolId } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicData();
  }, [poolId]);

  const fetchPublicData = async () => {
    try {
      const response = await axios.get(`${API}/public/${poolId}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch public data:', error);
      setError('Failed to load pool data');
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  // Prepare chart data
  const chartData = data?.readings?.slice(0, 30).reverse().map(r => ({
    date: new Date(r.date).toLocaleDateString(),
    pH: r.ph,
    Chlorine: r.chlorine,
    Alkalinity: r.alkalinity
  })) || [];

  // Compliance Badge Component
  const ComplianceBadge = ({ reading }) => {
    const status = getReadingStatus(reading);
    
    if (status.compliant) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Compliant
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Out of Range
      </span>
    );
  };

  // Value with indicator
  const ValueWithIndicator = ({ parameter, value }) => {
    const compliance = checkCompliance(parameter, value);
    
    if (compliance.status === 'none' || compliance.status === 'compliant') {
      return <span className="font-mono">{value}</span>;
    }
    
    return (
      <span className={`font-mono font-medium ${
        compliance.status === 'low' ? 'text-amber-600' : 'text-red-600'
      }`}>
        {value}
      </span>
    );
  };

  // LSI Badge Component
  const LSIBadge = ({ reading }) => {
    const lsi = calculateSaturationIndex(reading);
    const status = getLSIStatus(lsi);
    
    return (
      <div className="flex items-center space-x-2">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${status.color}`}>
          {status.label}
        </span>
        <span className="font-mono text-sm font-medium">
          {lsi !== null ? lsi.toFixed(2) : 'N/A'}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-danger">{error || 'Pool not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-view-page">
      {/* Header with background image */}
      <div
        className="relative h-64 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7222568/pexels-photo-7222568.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center">
          <div className="inline-flex items-center justify-center mb-4 bg-white rounded-lg p-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_93593000-b3be-431f-a37f-02f4cf188c0a/artifacts/x5wz1hkl_img_1_1724980327806.jpg" 
              alt="AquaPro Logs" 
              className="h-12"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">{data.pool.pool_name}</h1>
          <p className="text-white/90 text-lg">{data.pool.property_name}</p>
        </div>
        <div className="absolute top-4 right-4 flex space-x-2">
          <Button
            data-testid="public-contact-button"
            onClick={() => window.location.href = 'mailto:aquaprobcs@gmail.com?subject=Pool Compliance Inquiry&body=Pool: ' + data.pool.pool_name}
            variant="ghost"
            className="text-white hover:bg-white/20"
            title="Contact Admin"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contact
          </Button>
          <Button
            data-testid="public-language-toggle"
            onClick={toggleLanguage}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            <Languages className="h-5 w-5 mr-2" />
            {i18n.language.toUpperCase()}
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Pool Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('publicView')}</CardTitle>
            <CardDescription>Pool compliance information - Last 90 Days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('address')}</p>
                <p className="text-base">{data.pool.address}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('contactInfo')}</p>
                <p className="text-base">{data.pool.contact_info}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('volume')}</p>
                <p className="text-base">{data.pool.volume}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Readings (30 days)</p>
                <p className="text-base font-mono text-primary text-2xl">{data.readings.length}</p>
              </div>
            </div>
            
            {/* VGB Compliance Document */}
            {data.pool.has_vgb_compliance && (
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">VGB Compliance Document</p>
                      <p className="text-sm text-muted-foreground">{data.pool.vgb_compliance_filename || 'VGB Compliance.pdf'}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(`${API}/public/${poolId}/vgb-compliance`, '_blank')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    View PDF
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chart */}
        {data.readings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Chemical Trends</CardTitle>
              <CardDescription>Last 90 Days</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="pH" stroke="#0091FF" strokeWidth={2} />
                  <Line type="monotone" dataKey="Chlorine" stroke="#10B981" strokeWidth={2} />
                  <Line type="monotone" dataKey="Alkalinity" stroke="#F59E0B" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Readings Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('readings')}</CardTitle>
            <CardDescription>Compliance measurements</CardDescription>
          </CardHeader>
          <CardContent>
            {data.readings.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('noData')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-accent">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">{t('date')}</th>
                      <th className="px-4 py-3 text-left font-medium">Compliance</th>
                      <th className="px-4 py-3 text-left font-medium">LSI</th>
                      <th className="px-4 py-3 text-left font-medium">{t('ph')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('chlorine')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('calciumHardness')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('alkalinity')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('waterTemp')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('cyanuricAcid')}</th>
                      <th className="px-4 py-3 text-left font-medium">{t('tds')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.readings.map((reading) => (
                      <tr key={reading.id} className="border-b hover:bg-accent/50">
                        <td className="px-4 py-3 font-mono">
                          {new Date(reading.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <ComplianceBadge reading={reading} />
                        </td>
                        <td className="px-4 py-3">
                          <LSIBadge reading={reading} />
                        </td>
                        <td className="px-4 py-3">
                          <ValueWithIndicator parameter="ph" value={reading.ph} />
                        </td>
                        <td className="px-4 py-3">
                          <ValueWithIndicator parameter="chlorine" value={reading.chlorine} />
                        </td>
                        <td className="px-4 py-3">
                          <ValueWithIndicator parameter="calcium_hardness" value={reading.calcium_hardness} />
                        </td>
                        <td className="px-4 py-3">
                          <ValueWithIndicator parameter="alkalinity" value={reading.alkalinity} />
                        </td>
                        <td className="px-4 py-3 font-mono">{reading.water_temperature}</td>
                        <td className="px-4 py-3">
                          <ValueWithIndicator parameter="cyanuric_acid" value={reading.cyanuric_acid} />
                        </td>
                        <td className="px-4 py-3 font-mono">{reading.total_dissolved_solids}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-6 space-y-3">
          <p className="font-medium">AquaPro Logs - Pool Compliance Management System</p>
          <p>Texas Pool Code Compliance</p>
          <div className="flex items-center justify-center space-x-2">
            <Mail className="h-4 w-4" />
            <a 
              href="mailto:aquaprobcs@gmail.com" 
              className="text-primary hover:underline font-medium"
            >
              aquaprobcs@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicViewPage;